import { useEffect, useRef, useState } from "react"
import { CONCEPT_REPLAY_DETAIL_OPTIONS } from "./conceptReplay"
import { csrfToken } from "./rails"

export function watchReplayVersion({ path, detail, conceptKey, create = false, onResult }) {
  const controller = new AbortController()
  let timer = null

  async function request(create) {
    try {
      const url = new URL(path, window.location.origin)
      url.searchParams.set("detail", detail)
      if (conceptKey) url.searchParams.set("concept_key", conceptKey)
      const response = await fetch(url, {
        method: create ? "POST" : "GET",
        credentials: "same-origin",
        signal: controller.signal,
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        ...(create ? { body: JSON.stringify({ retry: true }) } : {})
      })
      const payload = await response.json()
      if (!response.ok || !payload.version) throw new Error(payload.error || "The saved summary couldn't be loaded. Please retry.")
      if (controller.signal.aborted) return
      onResult(payload)
      const version = payload.version
      if (["analyzing", "pending", "generating"].includes(version.status) || (version.status === "missing" && payload.canPrepare) || ["pending", "generating"].includes(version.narration?.status)) {
        timer = window.setTimeout(() => request(false), 4000)
      }
    } catch (error) {
      if (!controller.signal.aborted) onResult({
        error: error instanceof TypeError || error instanceof SyntaxError
          ? "The saved summary couldn't be loaded. Please retry."
          : error.message
      })
    }
  }

  request(create)
  return () => {
    controller.abort()
    window.clearTimeout(timer)
  }
}

export function useReplayVersion({ path, detail, conceptKey, active, sourceSignature }) {
  const [result, setResult] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const preparationRequested = useRef(false)
  const key = JSON.stringify([path, detail, conceptKey, sourceSignature, attempt])

  useEffect(() => {
    if (!active || !path) return undefined
    const create = preparationRequested.current
    preparationRequested.current = false
    return watchReplayVersion({ path, detail, conceptKey, create, onResult: (payload) => setResult({ key, ...payload }) })
  }, [active, key]) // eslint-disable-line react-hooks/exhaustive-deps

  const current = result?.key === key ? result : null
  const version = current?.version?.status === "ready" ? current.version : null
  return {
    version,
    options: current?.options || CONCEPT_REPLAY_DETAIL_OPTIONS.map((option) => ({ ...option, durationMs: option.targetMs, available: true })),
    selectedDetail: current?.selectedDetail || detail,
    status: current?.error ? "load_failed" : current?.version?.status || "loading",
    canPrepare: current?.canPrepare === true,
    error: current?.error || current?.version?.error || (!path && active ? "The replay story is not available yet." : null),
    retry: () => {
      preparationRequested.current = current?.canPrepare === true && !current?.error
      setAttempt((value) => value + 1)
    }
  }
}

export function replayPreparationState({ status, version, error, canPrepare, selectedDetail }) {
  const detail = (selectedDetail || "balanced").toLowerCase()
  if (error) return { error, action: status === "load_failed" || canPrepare ? "Retry" : null }
  if (status === "loading") return { message: `Loading your saved ${detail} summary…` }
  if (status === "analyzing") return { message: "Analyzing commit history…" }
  if (status === "missing") return { message: `No saved ${detail} summary is available yet.`, action: canPrepare ? "Generate summary" : null }
  if (status === "pending") return { message: `Your ${detail} summary is queued for generation…` }
  if (status === "generating") return { message: `Generating your ${detail} summary…` }

  switch (version?.narration?.status) {
    case "pending": return { message: "Your summary is saved. Narration is queued…" }
    case "generating": return { message: "Your summary is saved. Generating narration…" }
    case "failed": return { error: "Your summary is saved, but narration generation failed.", action: canPrepare ? "Retry narration" : null }
    case "missing": return { message: "Your summary is saved. Its narration is missing.", action: canPrepare ? "Generate narration" : null }
    case "ready": return { message: "Loading saved narration…" }
    default: return { message: "Your summary is saved. Narration is unavailable." }
  }
}
